import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import type { NotificationItem } from '@/lib/api/apiTypes';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import {
  BottomSheetBackdrop,
  BottomSheetModal,
} from '@gorhom/bottom-sheet';
import type { BottomSheetDefaultBackdropProps } from '@gorhom/bottom-sheet/lib/typescript/components/bottomSheetBackdrop/types';
import {
  BellOffIcon,
  MoreHorizontalIcon,
  ThumbsDownIcon,
  XIcon,
  type LucideIcon,
} from 'lucide-react-native';
import * as React from 'react';
import { Platform, Pressable, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { FullWindowOverlay } from 'react-native-screens';

export function PulseAlertActionSheet({
  bottomSheetRef,
  canMute,
  canMarkNotForMe,
  item,
  onClose,
  onMarkNotForMe,
  onMute,
}: {
  bottomSheetRef: React.RefObject<BottomSheetModal | null>;
  canMute: boolean;
  canMarkNotForMe: boolean;
  item: NotificationItem | null;
  onClose: () => void;
  onMarkNotForMe: (item: NotificationItem) => void;
  onMute: (item: NotificationItem) => void;
}) {
  const insets = useSafeAreaInsets();
  const snapPoints = React.useMemo(() => [canMute && canMarkNotForMe ? 286 : 224], [
    canMarkNotForMe,
    canMute,
  ]);
  const renderBackdrop = React.useCallback(
    (props: BottomSheetDefaultBackdropProps) => (
      <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} opacity={0.38} />
    ),
    []
  );

  const handleMute = React.useCallback(() => {
    if (!item) {
      return;
    }

    onMute(item);
  }, [item, onMute]);

  const handleNotForMe = React.useCallback(() => {
    if (!item) {
      return;
    }

    onMarkNotForMe(item);
  }, [item, onMarkNotForMe]);

  return (
    <BottomSheetModal
      ref={bottomSheetRef}
      index={0}
      snapPoints={snapPoints}
      enableDynamicSizing={false}
      detached
      animateOnMount
      enablePanDownToClose
      bottomInset={Math.max(insets.bottom, 12)}
      onDismiss={onClose}
      backdropComponent={renderBackdrop}
      containerComponent={Platform.OS === 'ios' ? (FullWindowOverlay as any) : undefined}
      backgroundStyle={{
        backgroundColor: DESIGN_TOKENS.colors.surfaceOverlay,
        borderRadius: 28,
      }}
      handleIndicatorStyle={{
        backgroundColor: DESIGN_TOKENS.colors.borderStrong,
        width: 48,
        height: 6,
      }}
      style={{
        marginHorizontal: 16,
        shadowColor: '#000000',
        shadowOpacity: 0.28,
        shadowRadius: 18,
        shadowOffset: { width: 0, height: 8 },
      }}>
      <View
        style={{
          paddingBottom: 18,
          paddingHorizontal: 16,
          paddingTop: 4,
        }}>
        <View className="flex-row items-start justify-between gap-3">
          <View className="min-w-0 flex-1 gap-1">
            <View className="flex-row items-center gap-2">
              <View className="size-8 items-center justify-center rounded-full bg-primary/15">
                <Icon as={MoreHorizontalIcon} size={15} className="text-primary" />
              </View>
              <Text className="text-lg font-semibold tracking-tight text-foreground">
                Tune this alert
              </Text>
            </View>
            <Text variant="small" className="text-muted-foreground">
              Keep Pulse useful, not noisy.
            </Text>
          </View>

          <Pressable
            accessibilityLabel="Close alert options"
            accessibilityRole="button"
            className="size-10 items-center justify-center rounded-full border border-border bg-input/30"
            onPress={onClose}>
            <Icon as={XIcon} size={16} className="text-muted-foreground" />
          </Pressable>
        </View>

        <View className="mt-5 gap-2">
          {canMute ? (
            <PulseAlertAction
              body="Manage settings for this type."
              icon={BellOffIcon}
              label="Mute this type"
              onPress={handleMute}
            />
          ) : null}

          {canMarkNotForMe ? (
            <PulseAlertAction
              body="Hide this and improve your feed."
              destructive
              icon={ThumbsDownIcon}
              label="Not for me"
              onPress={handleNotForMe}
            />
          ) : null}

          {!canMute && !canMarkNotForMe ? (
            <View className="rounded-[20px] border border-border bg-input/20 p-4">
              <Text className="text-[14px] font-semibold text-foreground">
                No extra controls yet
              </Text>
              <Text variant="small" className="mt-1 text-muted-foreground">
                Swipe to dismiss.
              </Text>
            </View>
          ) : null}
        </View>
      </View>
    </BottomSheetModal>
  );
}

function PulseAlertAction({
  body,
  destructive = false,
  icon,
  label,
  onPress,
}: {
  body: string;
  destructive?: boolean;
  icon: LucideIcon;
  label: string;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      className="flex-row items-center gap-3 rounded-[20px] border border-border bg-input/20 px-3.5 py-3 active:bg-surface-alt/70"
      onPress={onPress}>
      <View
        className={
          destructive
            ? 'size-10 items-center justify-center rounded-full bg-destructive/12'
            : 'size-10 items-center justify-center rounded-full bg-primary/15'
        }>
        <Icon
          as={icon}
          size={17}
          className={destructive ? 'text-destructive' : 'text-primary'}
        />
      </View>
      <View className="min-w-0 flex-1 gap-0.5">
        <Text className="text-[14px] font-semibold text-foreground">{label}</Text>
        <Text variant="small" className="text-muted-foreground" numberOfLines={2}>
          {body}
        </Text>
      </View>
    </Pressable>
  );
}
